import { DataTable } from "../components/data-table";
import React, { useState, useCallback, useMemo, useEffect, useRef, startTransition } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { manualCertificationStatusInfo } from "../../../constant";
import { ImageView } from "../../../components/Image";
import { getInitial, isAdmin, isManager } from "../../../services/utils";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../../services/app-context";
import { Card } from "../../../components/Card";
import { ReactComponent as MoreIcon } from "../../../assets/icons/more.svg";
import { ReactComponent as PersonIcon } from "../../../assets/icons/person.svg";
import { ReactComponent as CalendarIcon } from "../../../assets/icons/calendar-2.svg";
import { api } from "../../../services/api";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { SelectInput } from "../../../components/SelectInput/SelectInput";
import SearchView from "../../engagement-report/components/SearchView";
import UIPopover from "../../../components/UIPopover";
import { ActionView } from "./components/ActionView";
import { useUser } from "../../../services/user-context";
import dayjs from "dayjs";
import { twMerge } from "tailwind-merge";
import { StatusInputSelect } from "../manager-certification/components/StatusInputSelect";
import toast from "react-hot-toast";
import { Skeleton } from "../../../components/Skeleton";
import { getLabel, getUrl } from "../utils/labels";
import { StatusTabs } from "./components/StatusTabs";
import { isAIRecommendationEnabled, isCertificationOverallSummaryEnabled } from "../../../integrations/posthog/featureFlags";

export function WorkflowStatusCell({ status, className, certificationAssets }: { status: string, className?: string, certificationAssets?: any }) {
  const statusData = certificationAssets?.userStatus?.[status] || certificationAssets?.userStatus?.pending;

  return (
    <div
      className={twMerge("w-[102px] h-[37px] gap-0.5 flex items-center justify-start font-medium text-[13px]", status === "pending" ? "text-gray-402" : "text-black", className)}
    >
      {statusData?.label}
    </div>
  );
}

export function StatusCell({ status, className, certificationAssets, manuallyOverriden, aiFailedCount }: { status: string, className?: string, certificationAssets?: any, manuallyOverriden?: boolean, aiFailedCount?: number }) {
  const statusData = manuallyOverriden ? manualCertificationStatusInfo : (certificationAssets?.userStatus?.[status] || certificationAssets?.userStatus?.pending);

  return (
    <div
      className={twMerge("rounded-md w-[102px] h-[37px] gap-0.5 flex items-center justify-center font-medium text-[13px]", className)}
      style={{
        backgroundColor: statusData?.backgroundColor,
        color: statusData?.textColor,
        // color: statusData?.color,
      }}
    >
      {aiFailedCount !== undefined &&
        <>{aiFailedCount || ""}</>
      }
      {statusData?.label}
    </div>
  );
}

export const buildReviewUrl = (url: string, assignmentID: string) => {
  return `/${url}/review/${assignmentID}`;
}

const canReview = (data: any, role?: string, showOverallSummary?: boolean) => {
  if (showOverallSummary) {
    return isManager(role || "") || isAdmin(role || "");
  }
  if (isManager(role || "")) {
    return true;
  } else if (isAdmin(role || "")) {
    return data.isComplete === true;
  }
  return false;
}

const getColumns = (
  { onAction, showOverallSummary }: { onAction: (row: any) => void, showOverallSummary?: boolean },
  certificationAssets?: any,
  role?: string,
  onStatusChange?: (row: any, status: string) => void,
  showAIRecommendation?: boolean,
) => {
  const labels = certificationAssets?.labels || {};
  return [
    {
      accessorKey: "userId",
      header: ({ column }) => {
        return (
          <div
            className="p-0 min-w-[200px]"
          >
            Team Member
          </div>
        );
      },
      cell: ({ row }) => {
        return (
          <div
            className="flex items-center gap-2"
          >
            {row.original.user?.profileImg ? (
              <ImageView
                className="w-8 h-8 rounded-full object-cover"
                src={row.original.user?.profileImg}
                alt="user"
              />
            ) : (
              <div className="bg-primary w-8 h-8 rounded-full text-text-secondary text-lg flex justify-center items-center font-semibold">
                {getInitial(
                  row.original.userName?.split(" ")[0],
                  row.original.userName?.split(" ")[1]
                )}
              </div>
            )}
            <div>
              {row.original.userName}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "attempts",
      header: ({ column }) => {
        return <div className="w-full flex justify-start">{getLabel("attempt.plural", labels)}</div>;
      },
      cell: ({ row }) => {
        return (<div className="w-full flex justify-start flex-col">
            <span className="text-text2 text-sm font-semibold">{row.original.attemptCount} / {row.original?.attemptsAllowed || 0}</span>
          {(row.original?.attemptsRequired ?? 0) > 0 && (
            <span className="text-gray-601 text-xs">{row.original?.attemptsRequired} required</span>
          )}
        </div>);
      },
    },
    ...(showAIRecommendation ? [{
      id: "aiRec",
      accessorKey: "aiStatus",
      header: ({ column }) => {
        return <div className="p-0">AI Rec</div>;
      },
      cell: ({ row }) => {
        return <StatusCell status={row.original.aiStatus} aiFailedCount={row.original.aiFailedCount} certificationAssets={certificationAssets} manuallyOverriden={row.original.manuallyOverriden} />;
      },
    }] : []),
    ...(showOverallSummary ? [{
      accessorKey: "workflowStatus",
      header: ({ column }) => {
        return <div className="p-0 min-w-[200px]">Workflow progress</div>;
      },
      cell: ({ row }) => {
        return <WorkflowStatusCell status={row.original.workflowStatus} certificationAssets={certificationAssets} />;
      },
    }] : []),
    {
      accessorKey: "evaluationStatus",
      header: ({ column }) => {
        return <div className="p-0">{getLabel("status.singular", labels)}</div>;
      },
      cell: ({ row }) => {
        const roundedClass = showOverallSummary ? "!rounded-full" : "";
        const status = row.original.evaluationStatus;
        const canAdminOverride = isAdmin(role) && ["passed", "failed"].includes(status);
        return canAdminOverride ?
          <StatusInputSelect
            className={twMerge("!h-[37px] !w-[115px]", roundedClass)}
            value={status}
            onChange={(value) => { onStatusChange?.(row.original, value); }}
            certificationAssets={certificationAssets}
          /> :
          <StatusCell
            className={twMerge("!w-[115px]", roundedClass)}
            status={status}
            certificationAssets={certificationAssets}
          />;
      },
    },

    {
      id: "actions",
      header: () => {
        return <div className="w-full flex justify-center"></div>;
      },
      cell: ({ row }) => {
        const isDisabled = !canReview(row.original, role, showOverallSummary);
        return (
          <div className="w-full flex justify-center">
            <button
              onClick={() => {
                onAction(row.original);
              }}
              className={twMerge("text-sm text-center text-text2 hover:text-primary3", isDisabled && "opacity-50 cursor-not-allowed")}
              disabled={isDisabled}
            >
              Review
            </button>
          </div>
        );
      },
    },
  ];
};

type User = {
  id: string;
  name: string;
  email: string;
  joinedOn: string;
  tier: string;
  totalSellers: number;
  status: string;
  statusText: string;
  statusDescription: string;
  totalSpend: string;
  stripeCustomerId: string;
  subscription: any;
  openDrawer: (row: any) => void;
  deleteModal: (row: any) => void;
  deactivateModal: (row: any) => void;
};

const AssignmentTableSection = React.memo(function AssignmentTableSection(props: {
  tableKey: string;
  columns: ColumnDef<User>[];
  onRowClick: (row: any) => void;
  isRowClickable: (row: any) => boolean;
  data: any[];
  getRowId: (row: any, index: number) => string;
  isLoading: boolean;
  className?: string;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  footerContent?: React.ReactNode;
}) {
  return (
    <DataTable
      key={props.tableKey}
      columns={props.columns}
      onRowClick={props.onRowClick}
      isRowClickable={props.isRowClickable}
      data={props.data}
      getRowId={props.getRowId}
      isLoading={props.isLoading}
      className={props.className}
      scrollContainerRef={props.scrollContainerRef}
      footerContent={props.footerContent}
    />
  );
});

export function CertificationAssignmentList({ selectedCertification, onExportCertification, onDeleteCertification, isCertificationLoading, onEditCertification, onAddAssignees, onDuplicateCertification }: { selectedCertification: any, onExportCertification: () => void, onDeleteCertification: () => void, isCertificationLoading: boolean, onEditCertification: () => void, onAddAssignees: () => void, onDuplicateCertification: () => void }) {
  const navigate = useNavigate();
  const { certificationAssets, tenantProfile } = useApp();
  const routeNames = tenantProfile?.routeNames ?? {};
  const url = getUrl("certificationRoute", routeNames);
  const { userInfo } = useUser() as any;
  const role = userInfo?.role;
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    aiRecommendation: "",
  });
  const [localFilters, setLocalFilters] = useState({
    search: "",
    status: "",
    aiRecommendation: "",
  });
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    setLocalFilters((prev) => {
      const next = { ...filters };
      // Don't overwrite search while debounce is pending (avoids wiping typed search when status/AI Rec change first)
      if (searchDebounceRef.current !== null) {
        next.search = prev.search;
      }
      return next;
    });
  }, [filters.search, filters.status, filters.aiRecommendation]);
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const applyFilterUpdate = useCallback((key: string, value: string) => {
    startTransition(() => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    });
  }, []);

  const handleFilterChange = useCallback((key: string, value: string) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
    if (key === "search") {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        searchDebounceRef.current = null;
        applyFilterUpdate(key, value);
      }, 300);
    } else {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => applyFilterUpdate(key, value));
      });
    }
  }, [applyFilterUpdate]);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();
  const assignmentsQueryKey = useMemo(
    () => ["certification-assignments", selectedCertification?.id, filters],
    [selectedCertification?.id, filters]
  );

  const {
    data: assignmentData,
    isLoading: isLoadingAssignments,
    refetch: refetchAssignments,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: assignmentsQueryKey,
    queryFn: ({ pageParam }) => api.getCertificationAssignments(selectedCertification?.id, { ...filters, after: pageParam }),
    getNextPageParam: (lastPage) => lastPage?.data?.links?.after || undefined,
    enabled: !!selectedCertification?.id,
    cacheTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (!sentinelRef.current || !scrollContainerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '200px',
        threshold: 0.1,
      }
    );

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const onStatusChange = useCallback((row: any, status: string) => {
    const data = {
      verdict: status === "passed" ? true : false,
    };
    // Optimistically update the cached row so the UI reflects the change immediately,
    // and re-apply after the refetch since the server's evaluationStatus may not update right away.
    const previousData = queryClient.getQueryData(assignmentsQueryKey);
    const applyOptimisticStatus = () => {
      queryClient.setQueryData(assignmentsQueryKey, (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: {
              ...page.data,
              data: page.data?.data?.map((item: any) =>
                item.assignmentID === row.assignmentID ? { ...item, evaluationStatus: status } : item
              ),
            },
          })),
        };
      });
    };
    applyOptimisticStatus();
    toast.loading("Updating status...");
    api.updateAdminReview(row.assignmentID, data).then(() => {
      toast.dismiss();
      toast.success("Status updated successfully");
      // Refetch in the background; re-apply optimistic afterward in case server
      // returns stale evaluationStatus. Refetch errors are intentionally swallowed
      // here — the update succeeded, so the optimistic value is correct.
      // invalidateQueries runs in finally so it fires regardless of refetch outcome
      // and after the assignments refetch, giving the backend time to commit
      // the cert-level count update before the certifications-list refetch runs.
      refetchAssignments()
        .then(() => { applyOptimisticStatus(); })
        .catch(() => { })
        .finally(() => {
          queryClient.invalidateQueries({ queryKey: ["certifications-list"] });
        });
    }).catch((err) => {
      queryClient.setQueryData(assignmentsQueryKey, previousData);
      toast.dismiss();
      const message = err?.response?.data?.message || "Failed to update status";
      toast.error(message);
    });
  }, [refetchAssignments, queryClient, assignmentsQueryKey]);


  const onRowClick = useCallback((row: any) => {
    navigate(buildReviewUrl(url, row.assignmentID));
  }, [navigate, url]);

  const showAIRecommendation = isAIRecommendationEnabled();
  const showOverallSummary = isCertificationOverallSummaryEnabled();

  const columns: ColumnDef<User>[] = useMemo(
    () => getColumns({ onAction: onRowClick, showOverallSummary }, certificationAssets, role, onStatusChange, showAIRecommendation),
    [certificationAssets, role, onRowClick, onStatusChange, showAIRecommendation, showOverallSummary]
  );


  const isRowClickable = useCallback((row: any) => canReview(row, role, showOverallSummary), [role, showOverallSummary]);

  const getRowId = useCallback((row: any, index: number) => row.assignmentID ?? row.id ?? String(index), []);

  const assignments = useMemo(
    () => assignmentData?.pages?.flatMap((page) => page?.data?.data ?? []) ?? [],
    [assignmentData?.pages]
  );
  const metaData = assignmentData?.pages?.[0]?.data?.metadata || {};
  const [localCalendarDateStr, setLocalCalendarDateStr] = useState(() =>
    dayjs().format("YYYY-MM-DD")
  );
  useEffect(() => {
    const sync = () => {
      const next = dayjs().format("YYYY-MM-DD");
      setLocalCalendarDateStr((prev) => (prev !== next ? next : prev));
    };
    sync();
    const intervalId = window.setInterval(sync, 60_000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") sync();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
  const isPastDue = useMemo(() => {
    const dueDate = metaData?.dueDate;
    if (dueDate == null) return false;
    // Calendar dates in the viewer's local zone — matches "Due On" (dayjs(dueDate).local()).
    // Avoid dayjs.utc(dueDate).utcOffset(...) which anchors ISO dates at 00:00 UTC and can shift the formatted calendar day backward.
    const dueStr = dayjs(dueDate).format("YYYY-MM-DD");
    return localCalendarDateStr > dueStr;
  }, [metaData?.dueDate, localCalendarDateStr]);
  const filterOptions = certificationAssets?.filterOptions || {};

  const options = useMemo(
    () => [{ label: "All", value: "all", count: selectedCertification?.totalAssignments }, ...(filterOptions?.certificationStatus?.map((status: string) => ({
      label: certificationAssets?.userStatus?.[status]?.label,
      value: status,
      count: selectedCertification?.counts?.find((count: any) => count.status === status)?.count
    })) ?? [])],
    [certificationAssets?.userStatus, filterOptions?.certificationStatus, selectedCertification]
  );

  const aiRecommendationOptions = useMemo(
    () => [{ label: "All AI Recommendations", value: "all" }, ...(filterOptions?.certificationAiStatus?.map((aiRecommendation: string) => ({
      label: certificationAssets?.userStatus?.[aiRecommendation]?.label,
      value: aiRecommendation
    })) ?? [])],
    [certificationAssets?.userStatus, filterOptions?.certificationAiStatus]
  );

  return (
    <Card className="h-full">
      <>
        {isCertificationLoading ? <div className="flex items-center justify-center h-full">
          <div className=" bg-slate-200 animate-pulse transition duration-50 w-full h-full rounded-[14px]"></div>
        </div> :
          !isCertificationLoading && !selectedCertification?.id ? <div className="flex items-center justify-center h-full">
            <div className="text-text2 text-sm font-medium leading-[15px]">
              No certification found.
            </div>
          </div> :
            <>
              <div className="flex items-center justify-between mb-4">
                <div className="flex flex-col gap-2">
                  <span className="text-primary text-lg font-semibold leading-[16px] capitalize">{selectedCertification?.certificationName}</span>
                  {!isLoadingAssignments ? <div className="text-[13px] font-light text-[#242424] gap-2.5 flex items-center">
                    <div className="flex items-center gap-1">
                      <PersonIcon className="[&_path]:fill-[#6A6A6A] [&_path]:stroke-none w-4 h-4" />
                      <div>
                        {metaData?.totalActiveAssignments} Assigned User{metaData?.totalActiveAssignments > 1 ? "s" : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <CalendarIcon className="w-4 h-4" />
                      <span>
                        Start Date: {dayjs(metaData?.startDate).local().format("MMM DD, YYYY")}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <CalendarIcon className="w-4 h-4" />
                      <span>
                        Due On: {dayjs(metaData?.dueDate).local().format("MMM DD, YYYY")}
                      </span>
                    </div>
                  </div> :
                    <div className="flex items-center gap-2.5">
                      <Skeleton className="w-24 h-4" />
                      <Skeleton className="w-32 h-4" />
                      <Skeleton className="w-32 h-4" />
                    </div>}
                </div>
                <div>
                  <UIPopover
                    trigger={
                      <span className="block p-2">
                        <MoreIcon className="w-4 h-4" />
                      </span>
                    }
                  >
                    {(close) => (
                      <ActionView
                        onDelete={() => {
                          onDeleteCertification();
                          close();
                        }}
                        onExport={() => {
                          onExportCertification();
                          close();
                        }}
                        onDuplicate={() => {
                          onDuplicateCertification();
                          close();
                        }}
                        isAdmin={isAdmin(role)}
                        disableEditAndAssignees={isPastDue}
                        onEdit={() => {
                          onEditCertification();
                          close();
                        }}
                        onAddAssignees={() => {
                          onAddAssignees();
                          close();
                        }}
                      />
                    )}
                  </UIPopover>
                </div>
              </div>
              <div className="flex items-end gap-2">
                {showAIRecommendation ? <>
                  <div className="flex flex-col gap-0.5 flex-1">
                    <span className="text-sm text-[#0B0B0B] font-medium">AI Rec</span>
                    <SelectInput
                      options={aiRecommendationOptions}
                      value={localFilters.aiRecommendation}
                      onChange={(e) => handleFilterChange("aiRecommendation", e.target.value)}
                      placeholder="AI Rec"
                      className="!py-2.5 !px-3 text-sm font-medium flex-[3] border border-[#E6E6E6] rounded bg-[#F6F6F6]"
                      containerClassName="flex-[3]"
                    />
                  </div>
                  <div className="flex flex-col gap-0.5 flex-1">
                    <span className="text-sm text-[#0B0B0B] font-medium">Status</span>
                    <SelectInput
                      options={options}
                      value={localFilters.status}
                      onChange={(e) => handleFilterChange("status", e.target.value)}
                      placeholder="Status"
                      className="!py-2.5 !px-3 text-sm font-medium flex-[2] border border-[#E6E6E6] rounded bg-[#F6F6F6]"
                      containerClassName="flex-[2]"
                    />
                  </div>
                  <SearchView value={localFilters.search} onChange={(e) => handleFilterChange("search", e.target.value)} className="!py-2.5 border border-[#E6E6E6] rounded bg-[#F6F6F6] [&_input]:!bg-[#F6F6F6] flex-1" />
                </> :
                  <>
                    <StatusTabs statusOptions={options} value={localFilters.status || "all"} onChange={(value) => handleFilterChange("status", value)} />
                    <div className="w-full pb-2 flex-1">
                      <SearchView value={localFilters.search} onChange={(e) => handleFilterChange("search", e.target.value)} className="!py-2.5 border border-[#E6E6E6] rounded bg-[#F6F6F6] [&_input]:!bg-[#F6F6F6] flex-1" />
                    </div>
                  </>}

              </div>
              <div className="flex flex-col gap-5">
                <AssignmentTableSection
                  tableKey={selectedCertification?.id ?? "none"}
                  columns={columns}
                  onRowClick={onRowClick}
                  isRowClickable={isRowClickable}
                  data={assignments}
                  getRowId={getRowId}
                  isLoading={isLoadingAssignments}
                  scrollContainerRef={scrollContainerRef}
                  footerContent={
                    <>
                      {hasNextPage && (
                        <div ref={sentinelRef} className="h-px w-full" />
                      )}
                      {isFetchingNextPage && (
                        <div className="text-center py-2 bg-white">
                          <span className="text-text-gray text-sm">Loading more...</span>
                        </div>
                      )}
                    </>
                  }
                  className="!border-none [&_tbody_tr_td:first-child]:!pl-2 [&_.table-head-row_th:first-child]:!pl-2 [&_.table-head-row_th:last-child]:!rounded-r-md [&_.table-head-row_th]:!bg-gray-100 [&_.table-head-row]:!border-b-0 [&_.table-head]:!bg-white [&_.table-head]:!pl-0 [&_tbody>tr>td:first-child]:!pl-0 [&_.table-head]:!pl-0 [&_.table-head-row]:!bg-gray-100 !h-[calc(100dvh-320px)]"
                />
              </div>
            </>}
      </>
    </Card>
  );
}
